import { envConfig } from "config/envConfig.ts"
import flagsmith from "flagsmith"
import { FlagsmithProvider, useFlags } from "flagsmith/react"
import { ComponentProps, useEffect, useMemo } from "react"
import { useParams } from "react-router"
import { useUserRole } from "services/users"

export const FlagsProvider = ({
	children,
}: {
	children: ComponentProps<typeof FlagsmithProvider>["children"]
}) => {
	const { scopeId } = useParams()
	const { data: { id, role, isProvider, isFieldSupportAgent } = {} } =
		useUserRole()
	const traits = useMemo(
		() => ({
			identity: id ?? null,
			role: role ?? null,
			organization: isProvider ? (scopeId ?? null) : null,
			team: isFieldSupportAgent ? (scopeId ?? null) : null,
		}),
		[id, isFieldSupportAgent, isProvider, scopeId, role],
	)

	useEffect(() => {
		flagsmith.setTraits(traits)
	}, [traits])

	return (
		<FlagsmithProvider
			flagsmith={flagsmith}
			options={{
				environmentID: envConfig.flagsmithKey(),
				realtime: false,
				traits,
			}}
		>
			{children}
		</FlagsmithProvider>
	)
}

/**
 * Available feature flags for UI control.
 */
export const FLAGS = {
	patientMonthlyStats: "patientmonthlystats",
	chatMessageEmail: "chatmessageemail",
	chatMessageInApp: "chatmessageinapp",
	patientBrochure: "patientbrochure",
	supportRequests: "supportrequests",
	adminEnrollments: "adminenrollments",
	adminUsers: "adminusers",
	helpDocsEmbed: "helpdocsembed",
	adminConfigs: "adminconfigs",
	clinicalNotes: "clinicalnotes",
	clinicalEscalations: "clinicalescalations",
	adminVisits: "adminvisits",
	adminCareOrganizations: "admincareorganizations",
} as const

export type Flag = keyof typeof FLAGS

export const useFlag = (...names: Flag[]) => {
	const flags = useFlags(names)

	return flags
}
